import { useState, useEffect } from 'react';
import { useAnalysisStore } from '../../store/analysisStore';
import AssessmentForm from './AssessmentForm';
import AssessmentResult from './AssessmentResult';
import {
  Box,
  Typography,
  Container,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Link,
  Button,
  Collapse,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import MoodOutlinedIcon from '@mui/icons-material/MoodOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const HOW_IT_WORKS_SEEN_KEY = 'trustIndex.howItWorks.seen';

const DIMENSIONS = [
  {
    label: 'Source Credibility',
    Icon: VerifiedOutlinedIcon,
    description: "Verifies the article's named sources and weighs the publisher's track record against third-party credibility ratings.",
  },
  {
    label: 'Reporting Quality',
    Icon: FactCheckOutlinedIcon,
    description: 'Checks the reporting itself for completeness, balance across perspectives, depth, and editorial transparency.',
  },
  {
    label: 'Emotional Framing',
    Icon: MoodOutlinedIcon,
    description: 'Measures emotional intensity and dominant feelings, flagging framing designed to provoke fear, anger, or outrage.',
  },
  {
    label: 'Manipulation Tactics',
    Icon: PsychologyOutlinedIcon,
    description: 'Spots loaded language, logical fallacies, diversion, social pressure, and cognitive-bias exploitation.',
  },
  {
    label: 'Synthetic Media',
    Icon: SmartToyOutlinedIcon,
    description: 'Determines whether the article or its imagery was generated by AI rather than produced by a human.',
  },
];

export default function HomeView() {
  const {
    currentAssessment,
    assessmentLoading,
    assessmentProgress,
    assessmentProgressLog,
    assessmentInputUrl,
    error,
    resetAssessment,
  } = useAnalysisStore();

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [selectedDimension, setSelectedDimension] = useState<number | null>(null);
  const [howItWorksOpen, setHowItWorksOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(HOW_IT_WORKS_SEEN_KEY) !== 'true';
  });

  const handleChipClick = (index: number) => {
    setSelectedDimension((current) => (current === index ? null : index));
  };

  const toggleHowItWorks = () => {
    setHowItWorksOpen((open) => {
      const next = !open;
      if (next) {
        window.localStorage.setItem(HOW_IT_WORKS_SEEN_KEY, 'true');
      } else {
        setSelectedDimension(null);
      }
      return next;
    });
  };

  useEffect(() => {
    if (howItWorksOpen) {
      window.localStorage.setItem(HOW_IT_WORKS_SEEN_KEY, 'true');
    }
  }, [howItWorksOpen]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (assessmentLoading) {
      setElapsedSeconds(0);
      interval = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [assessmentLoading]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const showInputCard = !assessmentLoading && !currentAssessment;
  const showResult = !assessmentLoading && !!currentAssessment;

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Google-style centered landing */}
      {showInputCard && (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 6,
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 720, textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 500,
                color: '#1a73e8',
                letterSpacing: '-0.02em',
                fontSize: { xs: '2.125rem', sm: '3rem' },
                mb: 1,
              }}
            >
              Trust Index
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#1967d2',
                fontWeight: 400,
              }}
            >
              High-performance news credibility analysis
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 720,
              p: { xs: 4, sm: 6 },
              borderRadius: 4,
              bgcolor: '#ffffff',
              border: '1px solid #dadce0',
              boxShadow: '0 1px 6px rgba(32, 33, 36, 0.08)',
              textAlign: 'center',
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {error}
              </Alert>
            )}

            <AssessmentForm />
          </Paper>

          <Box sx={{ width: '100%', maxWidth: 720, mt: 5, textAlign: 'center' }}>
            <Typography color="text.primary" variant="body1" sx={{ fontWeight: 500, mb: 1.5 }}>
              Take control of trust in news and information you read.
            </Typography>

            <Box
              component="button"
              type="button"
              onClick={toggleHowItWorks}
              aria-expanded={howItWorksOpen}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#1a73e8',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                fontWeight: 500,
                py: 0.5,
                px: 1,
                borderRadius: 1,
                transition: 'background-color 150ms ease',
                '&:hover': { bgcolor: '#f1f3f4' },
              }}
            >
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 18,
                  transition: 'transform 200ms ease',
                  transform: howItWorksOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
              Meet your 5 AI buddies who score the news for you
            </Box>

            <Collapse in={howItWorksOpen} timeout={250}>
              <Box sx={{ mt: 2.5 }}>
                <Typography color="text.secondary" variant="body1" sx={{ mb: 3 }}>
                  5 autonomous AI buddies use their critical thinking powers to analyze news and score your article against rigorous journalistic principles, bringing transparency to your news.
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: '#5f6368', fontStyle: 'italic', mb: 2.5 }}
                >
                  Click each AI buddy to see their mission.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.25,
                  }}
                >
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1.25 }}>
                {DIMENSIONS.slice(0, 3).map((dim, i) => {
                  const isActive = selectedDimension === i;
                  const Icon = dim.Icon;
                  return (
                    <Box
                      key={dim.label}
                      component="button"
                      type="button"
                      onClick={() => handleChipClick(i)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 2,
                        py: 1,
                        borderRadius: '999px',
                        border: '1px solid',
                        borderColor: isActive ? '#1a73e8' : '#aecbfa',
                        bgcolor: isActive ? '#1a73e8' : '#e8f0fe',
                        color: isActive ? '#ffffff' : '#1967d2',
                        fontFamily: 'inherit',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 150ms ease',
                        '&:hover': isActive
                          ? { bgcolor: '#1967d2', borderColor: '#1967d2' }
                          : { bgcolor: '#aecbfa', borderColor: '#1a73e8' },
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                      {dim.label}
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1.25 }}>
                {DIMENSIONS.slice(3).map((dim, i) => {
                  const realIndex = i + 3;
                  const isActive = selectedDimension === realIndex;
                  const Icon = dim.Icon;
                  return (
                    <Box
                      key={dim.label}
                      component="button"
                      type="button"
                      onClick={() => handleChipClick(realIndex)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 2,
                        py: 1,
                        borderRadius: '999px',
                        border: '1px solid',
                        borderColor: isActive ? '#1a73e8' : '#aecbfa',
                        bgcolor: isActive ? '#1a73e8' : '#e8f0fe',
                        color: isActive ? '#ffffff' : '#1967d2',
                        fontFamily: 'inherit',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 150ms ease',
                        '&:hover': isActive
                          ? { bgcolor: '#1967d2', borderColor: '#1967d2' }
                          : { bgcolor: '#aecbfa', borderColor: '#1a73e8' },
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                      {dim.label}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Collapse in={selectedDimension !== null} timeout={200} unmountOnExit>
              <Box
                sx={{
                  mt: 2,
                  mx: 'auto',
                  maxWidth: 560,
                  px: 3,
                  py: 2,
                  borderRadius: 2,
                  bgcolor: '#f8f9fa',
                  border: '1px solid #e8eaed',
                  textAlign: 'left',
                }}
              >
                {selectedDimension !== null && (
                  <>
                    <Typography variant="subtitle2" sx={{ color: '#1967d2', fontWeight: 600, mb: 0.5 }}>
                      {DIMENSIONS[selectedDimension].label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {DIMENSIONS[selectedDimension].description}
                    </Typography>
                  </>
                )}
              </Box>
            </Collapse>
              </Box>
            </Collapse>
          </Box>
        </Box>
      )}

      {/* Loading / Progress */}
      {assessmentLoading && (
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: '#e8f0fe',
              borderRadius: 3,
              border: '1px solid #aecbfa',
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
              <CircularProgress size={80} thickness={4} sx={{ color: '#1a73e8' }} />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" component="div" sx={{ color: '#1967d2' }} fontWeight="bold">
                  {formatTime(elapsedSeconds)}
                </Typography>
              </Box>
            </Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#1a73e8' }}>
              Assessing…
            </Typography>

            {/* Live step checklist driven by SSE progress events. Completed
                steps are checked off; the most recent one shows an active spinner. */}
            <Box sx={{ textAlign: 'left', maxWidth: 420, mx: 'auto', my: 2 }}>
              {(assessmentProgressLog.length ? assessmentProgressLog : [assessmentProgress || 'Starting assessment...']).map((step, i, arr) => {
                const isActive = i === arr.length - 1;
                return (
                  <Box key={`${i}-${step}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, opacity: isActive ? 1 : 0.6 }}>
                    {isActive ? (
                      <CircularProgress size={16} thickness={5} sx={{ color: '#1a73e8' }} />
                    ) : (
                      <CheckCircleIcon sx={{ fontSize: 18, color: '#34a853' }} />
                    )}
                    <Typography variant="body2" sx={{ color: '#1967d2', fontWeight: isActive ? 600 : 400 }}>
                      {step}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
              This assessment typically takes 45-90 seconds. The LLM is fetching the article, researching the publisher, and applying the full methodology.
            </Typography>
            {assessmentInputUrl && (
              <Box mt={2}>
                <Link
                  href={assessmentInputUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body1"
                  fontWeight="bold"
                  underline="hover"
                  sx={{ color: '#1a73e8' }}
                >
                  Read the article while you wait
                </Link>
              </Box>
            )}
          </Box>
        </Container>
      )}

      {/* Assessment Result */}
      {showResult && (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="bold">
              Latest Analysis Result
            </Typography>
            <Button
              variant="contained"
              startIcon={<RestartAltIcon />}
              onClick={resetAssessment}
              sx={{
                bgcolor: '#1a73e8',
                textTransform: 'none',
                borderRadius: '20px',
                '&:hover': { bgcolor: '#1967d2' },
              }}
            >
              New Assessment
            </Button>
          </Box>
          <Divider sx={{ mb: 4 }} />
          <AssessmentResult result={currentAssessment!} />
        </Container>
      )}
    </Box>
  );
}
